import { Component, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { forkJoin } from 'rxjs';
import { AdminService } from '../../services/AdminService/admin';
import { CompanyService } from '../../services/CompanyService/company';
import { AuthService } from '../../services/AuthService/auth';
import { GeneralTermResponse } from '../../models/country-config.dto';
import { extractErrorMessage } from '../../shared/error-utils';
import { ErrorBanner } from '../../shared/components/error-banner/error-banner';
import { EmptyState } from '../../shared/components/empty-state/empty-state';
import { findCountry } from '../../shared/countries';
import { buildLocalNameIndex, localNameFor } from '../../shared/term-mapping';
import { PageSizeSelect } from '../../shared/components/page-size-select/page-size-select';
import { OptionSelect } from '../../shared/components/option-select/option-select';
import { FilterOption } from '../../shared/components/filter-select/filter-select';

@Component({
  selector: 'app-country-term-compare',
  imports: [CommonModule, FormsModule, ErrorBanner, EmptyState, PageSizeSelect, OptionSelect],
  templateUrl: './country-term-compare.html',
  styleUrl: './country-term-compare.scss',
})
export class CountryTermCompare implements OnInit {
  generalTerms = signal<GeneralTermResponse[]>([]);
  availableCountries = signal<string[]>([]);
  isLoading = signal(false);
  errorMessage = signal('');

  leftCountry = '';
  rightCountry = '';
  termSearch = '';

  readonly pageSizeOptions = [10, 20, 50, 100];
  pageSize = signal(10);
  page = signal(0);

  private localNameIndex = new Map<string, Map<string, string>>();

  constructor(
    private admin: AdminService,
    private companyService: CompanyService,
    private auth: AuthService
  ) {}

  ngOnInit(): void {
    if (!this.auth.getSession()?.companyId) {
      this.load();
      return;
    }

    this.companyService.me().subscribe({
      next: (res) => {
        this.leftCountry = res.data.countryCode;
        this.load();
      },
      error: () => this.load(),
    });
  }

  load(): void {
    this.isLoading.set(true);
    this.errorMessage.set('');

    forkJoin({
      terms: this.admin.listGeneralTerms(),
      rules: this.admin.listTermRules(),
    }).subscribe({
      next: ({ terms, rules }) => {
        this.isLoading.set(false);
        this.generalTerms.set([...terms.data].sort((a, b) => a.term.localeCompare(b.term)));
        this.localNameIndex = buildLocalNameIndex(rules.data);

        const countries = Array.from(new Set(rules.data.map((r) => r.countryCode))).sort();
        this.availableCountries.set(countries);

        if (!this.leftCountry || !countries.includes(this.leftCountry)) {
          this.leftCountry = countries[0] ?? '';
        }
        if (!this.rightCountry || !countries.includes(this.rightCountry) || this.rightCountry === this.leftCountry) {
          this.rightCountry = countries.find((c) => c !== this.leftCountry) ?? '';
        }
      },
      error: (err: HttpErrorResponse) => {
        this.isLoading.set(false);
        this.errorMessage.set(extractErrorMessage(err, 'Failed to load country term mappings.'));
      },
    });
  }

  swap(): void {
    [this.leftCountry, this.rightCountry] = [this.rightCountry, this.leftCountry];
  }

  countryLabel(code: string): string {
    const country = findCountry(code);
    return country ? `${code} · ${country.name}` : code;
  }

  get countryOptions(): FilterOption[] {
    return this.availableCountries().map((code) => ({ value: code, label: this.countryLabel(code) }));
  }

  get visibleTerms(): GeneralTermResponse[] {
    const search = this.termSearch.trim().toLowerCase();
    const matching = search
      ? this.generalTerms().filter((t) => t.term.toLowerCase().includes(search))
      : this.generalTerms();
    return matching;
  }

  get totalCount(): number {
    return this.visibleTerms.length;
  }

  get totalPages(): number {
    return Math.max(1, Math.ceil(this.totalCount / this.pageSize()));
  }

  get pagedTerms(): GeneralTermResponse[] {
    const start = this.page() * this.pageSize();
    return this.visibleTerms.slice(start, start + this.pageSize());
  }

  get rangeStart(): number {
    return this.totalCount === 0 ? 0 : this.page() * this.pageSize() + 1;
  }

  get rangeEnd(): number {
    return Math.min((this.page() + 1) * this.pageSize(), this.totalCount);
  }

  /** Page numbers to render, with -1 as an ellipsis placeholder, e.g. [0, -1, 4, 5, 6, -1, 19]. */
  get pageWindow(): number[] {
    const total = this.totalPages;
    const current = this.page();
    const pages: number[] = [];
    const add = (p: number) => {
      if (pages[pages.length - 1] !== p) {
        pages.push(p);
      }
    };

    add(0);
    for (let p = current - 1; p <= current + 1; p++) {
      if (p > 0 && p < total - 1) {
        if (p > pages[pages.length - 1] + 1) {
          add(-1);
        }
        add(p);
      }
    }
    if (total - 1 > pages[pages.length - 1] + 1) {
      add(-1);
    }
    if (total > 1) {
      add(total - 1);
    }
    return pages;
  }

  onSearchChange(): void {
    this.page.set(0);
  }

  onPageSizeChange(size: number): void {
    this.pageSize.set(size);
    this.page.set(0);
  }

  goToPage(page: number): void {
    if (page < 0 || page >= this.totalPages || page === this.page()) {
      return;
    }
    this.page.set(page);
  }

  cell(term: string, countryCode: string): string {
    return localNameFor(term, countryCode, this.localNameIndex);
  }

  isOverride(term: string, countryCode: string): boolean {
    return this.cell(term, countryCode) !== term;
  }
}
